import type { AdminEmailActivity, AdminEmailMessage } from "@recital-master/shared";

export type AdminEmailSourceRecord = {
  id: string;
  data: Record<string, unknown>;
};

export type AdminAccountEmailRecord = {
  createdAt: unknown;
  primaryEmail: unknown;
};

function nullableIso(value: unknown): string | null {
  return typeof value === "string" && Number.isFinite(Date.parse(value)) ? value : null;
}

function count(value: unknown): number {
  return Math.max(0, Math.floor(Number(value) || 0));
}

export function buildAdminEmailActivity(
  sourceMessages: AdminEmailSourceRecord[],
  sourceAccounts: AdminAccountEmailRecord[],
  generatedAt = new Date().toISOString()
): AdminEmailActivity {
  const accountCreatedAtByEmail = new Map(
    sourceAccounts.flatMap((account) => {
      const email = typeof account.primaryEmail === "string"
        ? account.primaryEmail.trim().toLowerCase()
        : "";
      if (!email) return [];
      return [[email, nullableIso(account.createdAt) ?? generatedAt] as const];
    })
  );
  const messages: AdminEmailMessage[] = sourceMessages
    .map(({id, data: message}) => {
      const recipient = String(message.recipient ?? "").trim().toLowerCase();
      return {
        id,
        recipient,
        subject: String(message.subject ?? "Untitled email"),
        template: String(message.template ?? "campaign"),
        campaign: typeof message.campaign === "string" ? message.campaign : null,
        createdAt: nullableIso(message.createdAt) ?? generatedAt,
        acceptedAt: nullableIso(message.acceptedAt),
        deliveredAt: nullableIso(message.deliveredAt),
        firstOpenedAt: nullableIso(message.firstOpenedAt),
        lastOpenedAt: nullableIso(message.lastOpenedAt),
        humanOpenCount: count(message.humanOpenCount),
        automatedOpenCount: count(message.automatedOpenCount),
        firstClickedAt: nullableIso(message.firstClickedAt),
        lastClickedAt: nullableIso(message.lastClickedAt),
        humanClickCount: count(message.humanClickCount),
        automatedClickCount: count(message.automatedClickCount),
        temporaryFailedAt: nullableIso(message.temporaryFailedAt),
        permanentlyFailedAt: nullableIso(message.permanentlyFailedAt),
        failureReason: typeof message.failureReason === "string" ? message.failureReason : null,
        complainedAt: nullableIso(message.complainedAt),
        unsubscribedAt: nullableIso(message.unsubscribedAt),
        signedInAt: accountCreatedAtByEmail.get(recipient) ?? null
      };
    })
    .sort((left, right) => right.createdAt.localeCompare(left.createdAt));
  const humanEngaged = (message: AdminEmailMessage) =>
    message.humanOpenCount > 0 || message.humanClickCount > 0;
  const automatedOnly = (message: AdminEmailMessage) =>
    !humanEngaged(message) &&
    (message.automatedOpenCount > 0 || message.automatedClickCount > 0);

  return {
    totals: {
      sent: messages.filter((message) => message.acceptedAt != null).length,
      delivered: messages.filter((message) => message.deliveredAt != null).length,
      opened: messages.filter(humanEngaged).length,
      automatedOnly: messages.filter(automatedOnly).length,
      clicked: messages.filter((message) => message.humanClickCount > 0).length,
      failed: messages.filter((message) => message.permanentlyFailedAt != null).length,
      openedNotSignedIn: messages.filter((message) =>
        humanEngaged(message) && message.signedInAt == null
      ).length
    },
    messages
  };
}
